/**
 * Wire types for `GET /me/referrals` - the "Referrals & rewards" settings
 * section (`.design/onboarding-2026-08/POST_ONBOARDING_OBJECTIVES.md`
 * section 6). Personal, not space-scoped: the code belongs to the user.
 */

/**
 * The ONE shape of a personal referral code (`User.referralCode`), shared by
 * the backend minter/guards, the marketing `/r/<code>` route and the
 * attribution package's `ref` capture, so a value that is not a code never
 * becomes a referral signal anywhere. Lowercase alphanumerics of the minted
 * length; the DB decides whether it belongs to anyone.
 */
export const REFERRAL_CODE_SHAPE = /^[a-z0-9]{6,16}$/i;

/** True when `value` is a trimmed string shaped like a referral code. */
export const isReferralCodeShaped = (value: unknown): value is string =>
	typeof value === 'string' && REFERRAL_CODE_SHAPE.test(value.trim());

/** Where a referred company is on the road to the referrer's reward. */
export type ReferralStatus = 'signed_up' | 'activated' | 'reward_paid';

export interface ReferredCompany {
	/** The referee's space id (the unit the reward keys on). */
	refereeGraphId: string;
	/** Space name at read time; the referee can rename it. */
	refereeSpaceName: string | null;
	status: ReferralStatus;
	signedUpAt: string;
	activatedAt: string | null;
	rewardPaidAt: string | null;
	/** Referrer and referee share an email domain - queued for a look, not blocked. */
	sameDomain: boolean;
}

export type RewardSubmissionKind = 'referral' | 'post' | 'review';

export type RewardSubmissionStatus =
	| 'pending'
	| 'auto_approved'
	| 'approved'
	| 'rejected'
	| 'reversed';

export interface RewardSubmissionSummary {
	id: string;
	kind: RewardSubmissionKind;
	status: RewardSubmissionStatus;
	/** Catalogue objective id the row pays (referrals: '11'). */
	objectiveId: string | null;
	/** Space the reward was paid to. */
	spaceId: string | null;
	createdAt: string;
	resolvedAt: string | null;
	/** Until when an admin can still reverse an auto-approved payout. */
	reversibleUntil: string | null;
	/** Referral rows only: the referee space. */
	referralRefereeGraphId: string | null;
}

/** One paid reward, for the ledger. */
export interface RewardLedgerEntry {
	id: string;
	kind: RewardSubmissionKind;
	/** What was paid, in the user's words ("+1 month Team"). */
	label: string;
	spaceId: string | null;
	paidAt: string;
	reversed: boolean;
}

export interface MeReferralsResponse {
	/** Personal code; the link is `/r/<code>` on the marketing origin. */
	code: string;
	/** Path only - the web app prefixes the origin it runs on. */
	linkPath: string;
	referred: ReferredCompany[];
	submissions: RewardSubmissionSummary[];
	ledger: RewardLedgerEntry[];
}
